import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  AlloyButton,
  AlloyIcon,
  Dropdown,
  ModalContext
} from 'cat-ecommerce-alloy';
import { DrawerDropdown } from '@app/components/common/DrawerDropdown';
import Address from '../Address';
import AddressFormModal from '../AddressFormModal';
import AddressMobileDrawer from '../AddressMobileDrawer/AddressMobileDrawer';
import {
  getAddressForDisplay,
  getCountryByCode,
  getStatesInSelectedCountry,
  getStateByCode
} from '../utils';
import stylesModule from '@app/components/pages/summary-and-payment/styles.module.scss';
import { useBreakpoint } from '@app/hooks';
import { PAGE_BREAKPOINTS } from '@app/constants/commonConstants';
import { isSummaryAndPaymentPage } from '@app/utils/browserUtils';
import { formatName } from '@app/components/pages/checkout/utils';
import {
  PCC_PUD_TITLE_UPDATES,
  PCC_PS_BILLING_SECTION_DRAWERS,
  PCC_PS_TITLE_UPDATES,
  PCC_CHECKOUT_PAGES_DRAWERS_MOBILE
} from '@app/constants/featureFlags';
import { useSelector } from 'react-redux';
import { useDrawer } from '@cat-ecom/pcc-components';
import { useForm } from 'react-hook-form';
import isEmpty from 'lodash/isEmpty';
import styles from './AddressPicker.module.scss';

const AddressPicker = ({
  addressLimit,
  addresses = [],
  countries = [],
  defaultCountry,
  isGuest = false,
  isEditButtonDisabled = false,
  showAddAddressField = false,
  locale,
  onSubmit,
  readOnly,
  requiredFields,
  selected,
  setSelected,
  title,
  isPendingOrder,
  showPhoneNumberWithVerification = true,
  makeButtonsDynamic = false,
  isFullAddress,
  showTitleRequired,
  showAddressNickname = false,
  showNeighborhood = true,
  showAddButton = true,
  showFormLoader = false,
  isDealerUser = false,
  profileAddressId = '',
  labelClassName = '',
  showAddressLine2 = false,
  withDrawerDropdown = false,
  aviMethods,
  errorSelector,
  errorPath,
  ...props
}) => {
  const [t] = useTranslation();
  const modalCtx = useContext(ModalContext);
  const isLG = useBreakpoint(PAGE_BREAKPOINTS.LG);
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const iconSize = isLG ? 24 : 30;
  const iconWidthAndHeight = { width: iconSize, height: iconSize };
  const [isDirty, setIsDirty] = useState(false);

  const PUDTitleUpdatesFlag = useSelector(
    s => s.featureFlag?.[PCC_PUD_TITLE_UPDATES]
  );
  const PSTitleUpdatesFlag = useSelector(
    s => s.featureFlag?.[PCC_PS_TITLE_UPDATES]
  );

  const CheckoutDrawersMobileFlag = useSelector(
    s => s.featureFlag?.[PCC_CHECKOUT_PAGES_DRAWERS_MOBILE]
  );

  const hasTitleUpdatesFlag = PUDTitleUpdatesFlag || PSTitleUpdatesFlag;
  const billingSectionDrawerFlag = useSelector(
    s => s.featureFlag?.[PCC_PS_BILLING_SECTION_DRAWERS]
  );
  const addressSignature = JSON.stringify(addresses);

  const getAddressOptionDisplay = item => (item ? item.nickName : '');

  const getDisplayAddress = item => {
    const isJobsite = item?.nickName?.includes('JOBSITE');
    const nickname = isJobsite ? item?.nickName?.split('_')[1] : item?.nickName;
    return item ? formatName(nickname) : '';
  };

  const { displayName: countryDisplayNameInitial } = getCountryByCode(
    countries,
    selected?.country,
    {
      displayName: null
    }
  );

  const countryDisplayName = countryDisplayNameInitial || defaultCountry;

  const statesOrProvinces = getStatesInSelectedCountry(
    countries,
    countryDisplayName
  );

  const { displayName: stateDisplayName } = getStateByCode(
    statesOrProvinces,
    selected?.state,
    {
      displayName: selected?.state
    }
  );

  const initialAddressDrawerValues = {
    nickName: '',
    firstName: '',
    lastName: '',
    country: countryDisplayName,
    zipCode: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    phone1: '',
    email: '',
    neighborhood: ''
  };

  const addressDrawerMethods = useForm({
    defaultValues: initialAddressDrawerValues,
    mode: 'all'
  });

  const { reset } = addressDrawerMethods;

  const { closeDrawer, drawerProps, openDrawer } = useDrawer({
    onOpen: () => {
      reset({
        ...selected,
        country: countryDisplayName,
        state: stateDisplayName,
        neighborhood: isEmpty(selected?.neighborhood)
          ? ''
          : selected?.neighborhood
      });
    }
  });

  const mobileDrawerProps = {
    closeDrawer,
    addressDrawerMethods,
    ...drawerProps
  };

  const handleClose = () => modalCtx.removeCurrentModal();

  const handleSubmit = (form, stopFormLoader) => {
    setIsDirty(true);
    // expose the setSubmitting (false) from formik
    // so a external component or proces can stop the spinner in the modal form
    if (showFormLoader) {
      return onSubmit(form, stopFormLoader);
    }
    return onSubmit(form);
  };

  const showAddAddress =
    showAddAddressField && !selected && addresses.length === 0;

  const openAddressFormModal = address => {
    const initialFormikValues = {
      ...getAddressForDisplay({ address, countries })
    };
    modalCtx.addModalWithOptions(
      <AddressFormModal
        addressLimit={addressLimit}
        countries={countries}
        defaultCountry={defaultCountry}
        initialValues={initialFormikValues}
        isGuest={isGuest}
        onClose={() => handleClose()}
        onSubmit={handleSubmit}
        requiredFields={requiredFields}
        showPhoneNumberWithVerification={showPhoneNumberWithVerification}
        showNeighborhood={showNeighborhood}
        showAddButton={showAddButton}
        showFormLoader={showFormLoader}
        isDealerUser={isDealerUser}
        profileAddressId={profileAddressId}
        labelClassName={labelClassName}
        showAddressLine2={showAddressLine2}
      />,
      `${t('ADDR_EDIT_ADDRESS')}`,
      true
    );
  };

  const handleEdit = () => {
    if (!isMD && billingSectionDrawerFlag) {
      openDrawer();
    } else {
      openAddressFormModal(selected);
    }
  };

  // Update the selected address if the list of billing addresses changes
  useEffect(() => {
    const updatedAddress = addresses.find(
      ({ nickName }) => selected && selected.nickName === nickName
    );
    if (updatedAddress) {
      setSelected(updatedAddress);
    }
    if (isDealerUser && isSummaryAndPaymentPage()) {
      setSelected(addresses.slice(-1).pop());
    }
  }, [addressSignature]);

  // if the number of addresses have changed, then select the newest added one
  useEffect(() => {
    if (isDirty) {
      setSelected(addresses.slice(-1).pop());
    }
  }, [addresses.length]);

  const getAddressTitle = extraClass => {
    return (
      <div
        className={cx(stylesModule['col-print-5'], 'pb-1', {
          'd-flex align-items-center justify-content-between': showAddAddress
        })}
      >
        <p
          className={cx(
            'exclude-global input__label',
            extraClass,
            labelClassName,
            stylesModule['text-print-sm']
          )}
        >
          {`${title}${showTitleRequired ? '*' : ''}`}
        </p>
        {showAddAddress && (
          <AlloyButton
            buttonType="ghost"
            className={'d-flex align-items-center pe-0 pt-1 pt-lg-2'}
            size={makeButtonsDynamic ? 'dynamic' : 'small'}
            onClick={() => openAddressFormModal()}
          >
            <span>{t('ADD')}</span>
            <AlloyIcon
              icon="icon-plus"
              className="ps-2"
              {...(makeButtonsDynamic && iconWidthAndHeight)}
            />
          </AlloyButton>
        )}
      </div>
    );
  };

  return (
    <div className={cx('mb-2 ms-0', stylesModule['row-print'])}>
      {addresses.length > 1 ? (
        <>
          <div className="d-print-none">
            {withDrawerDropdown ? (
              <DrawerDropdown
                items={addresses}
                itemToString={getDisplayAddress}
                selected={selected}
                label={title}
                onChange={setSelected}
                {...props}
                isPaymentAndSummaryPage={isSummaryAndPaymentPage()}
              />
            ) : (
              <Dropdown
                items={addresses}
                itemToString={getDisplayAddress}
                keyToString={getAddressOptionDisplay}
                selected={selected}
                label={title}
                onChange={setSelected}
                {...props}
                data-cs-mask
                labelClassName={labelClassName}
              />
            )}
          </div>
          {getAddressTitle('d-none d-print-block')}
        </>
      ) : (
        getAddressTitle('d-block')
      )}
      {selected && (
        <div
          className={cx(
            'd-flex',
            stylesModule['mx-print-8'],
            stylesModule['col-print-6']
          )}
        >
          <div
            className={cx(
              'u-text-md flex-column flex-shrink-1 px-0 flex-grow-1'
            )}
          >
            {showAddressNickname && (
              <span className="color-dark-gray mb-3 mt-1 d-block">
                {selected.nickName || ''}
              </span>
            )}
            <Address
              {...getAddressForDisplay({ address: selected, countries })}
              className={cx(stylesModule['text-print-sm'], 'u-color--DarkGray')}
              locale={locale}
              isFullAddress={isFullAddress}
            />
          </div>
          {!readOnly && !isPendingOrder && (
            <div
              className={cx(
                'text-nowrap d-print-none',
                addresses.length > 1 ? 'mt-n1' : 'mt-n4'
              )}
            >
              <AlloyButton
                buttonType="ghost"
                className={cx('d-flex align-items-center p-0', {
                  'mt-5': showAddressNickname,
                  'text-capitalize': hasTitleUpdatesFlag,
                  [styles['address-picker__edit-button']]:
                    !isMD && hasTitleUpdatesFlag,
                  [styles['with-dropdown']]:
                    !isMD && hasTitleUpdatesFlag && addresses.length > 1
                })}
                size={makeButtonsDynamic ? 'dynamic' : 'small'}
                onClick={handleEdit}
                disabled={isEditButtonDisabled}
              >
                {t('CUST_EDIT')}
                <AlloyIcon
                  icon="icon-edit"
                  className="ps-2"
                  {...(makeButtonsDynamic && iconWidthAndHeight)}
                />
              </AlloyButton>
            </div>
          )}
        </div>
      )}
      <AddressMobileDrawer
        addresses={addresses}
        addressLimit={addressLimit}
        countries={countries}
        initialValues={selected}
        isGuest={isGuest}
        onSubmitForm={handleSubmit}
        setSelected={setSelected}
        requiredFields={requiredFields}
        showPhoneNumberWithVerification={showPhoneNumberWithVerification}
        showNeighborhood={showNeighborhood}
        showAddButton={showAddButton}
        showFormLoader={showFormLoader}
        isDealerUser={isDealerUser}
        profileAddressId={profileAddressId}
        labelClassName={labelClassName}
        showAddressLine2={showAddressLine2}
        aviMethods={aviMethods}
        countryDisplayName={countryDisplayName}
        withDropdownSearch={CheckoutDrawersMobileFlag}
        errorSelector={errorSelector}
        errorPath={errorPath}
        {...mobileDrawerProps}
      />
    </div>
  );
};

AddressPicker.propTypes = {
  addressLimit: PropTypes.string,
  addresses: PropTypes.arrayOf(
    PropTypes.shape({
      nickName: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      address1: PropTypes.string,
      address2: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zipCode: PropTypes.string,
      phone: PropTypes.string,
      email: PropTypes.string,
      addressId: PropTypes.string,
      addressType: PropTypes.string,
      default: PropTypes.oneOfType([PropTypes.string, PropTypes.bool])
    })
  ),
  countries: PropTypes.arrayOf(
    PropTypes.shape({
      states: PropTypes.arrayOf(
        PropTypes.shape({
          code: PropTypes.string,
          displayName: PropTypes.string
        })
      ),
      displayName: PropTypes.string.isRequired
    })
  ).isRequired,
  defaultCountry: PropTypes.string,
  isGuest: PropTypes.bool,
  showAddAddressField: PropTypes.bool,
  makeButtonsDynamic: PropTypes.bool,
  locale: PropTypes.string,
  name: PropTypes.string,
  onSubmit: PropTypes.func,
  readOnly: PropTypes.bool,
  requiredFields: PropTypes.arrayOf(PropTypes.string),
  selected: PropTypes.shape({
    nickName: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    address1: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zipCode: PropTypes.string,
    phone: PropTypes.string,
    email: PropTypes.string,
    addressId: PropTypes.string,
    addressType: PropTypes.string,
    default: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    country: PropTypes.string,
    neighborhood: PropTypes.string
  }),
  errorSelector: PropTypes.string,
  errorPath: PropTypes.string,
  setSelected: PropTypes.func,
  title: PropTypes.string.isRequired,
  showPhoneNumberWithVerification: PropTypes.bool,
  isPendingOrder: PropTypes.bool,
  showNeighborhood: PropTypes.bool,
  showAddButton: PropTypes.bool,
  showFormLoader: PropTypes.bool,
  isFullAddress: PropTypes.bool,
  showTitleRequired: PropTypes.bool,
  showAddressNickname: PropTypes.bool,
  isDealerUser: PropTypes.bool,
  profileAddressId: PropTypes.string,
  labelClassName: PropTypes.string,
  isEditButtonDisabled: PropTypes.bool,
  showAddressLine2: PropTypes.bool,
  withDrawerDropdown: PropTypes.bool,
  aviMethods: PropTypes.shape({
    isAVIMobileOpen: PropTypes.bool,
    setIsAVIMobileOpen: PropTypes.func,
    aviNotNeeded: PropTypes.bool,
    setAviNotNeeded: PropTypes.func,
    onAVIContinue: PropTypes.func,
    aviAddressData: PropTypes.shape({})
  })
};

export default AddressPicker;
